import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Pagination, FormControl, Select, MenuItem, Stack,
  Card, CardContent, Grid, Alert, Switch, FormControlLabel, Tooltip, IconButton,
  LinearProgress, Divider,
} from '@mui/material';
import {
  Payment, AccountBalance, CreditCard, TrendingUp, Receipt,
  CheckCircle, Error as ErrorIcon, Schedule, Autorenew, Info, Download,
} from '@mui/icons-material';
import { paymentsAPI } from '../../api/client';
import { format } from 'date-fns';

const statusColors = {
  pending: 'warning', processing: 'info', completed: 'success',
  failed: 'error', refunded: 'default', partially_refunded: 'secondary',
};
const statusIcons = {
  pending: <Schedule fontSize="small" />, completed: <CheckCircle fontSize="small" />,
  failed: <ErrorIcon fontSize="small" />, refunded: <Autorenew fontSize="small" />,
};
const typeLabels = {
  rent: 'Rent', deposit: 'Deposit', service_fee: 'Service Fee',
  refund: 'Refund', penalty: 'Penalty',
};
const gatewayLogos = {
  paystack: '🟢 Paystack', stripe: '🟣 Stripe', manual: '📋 Manual',
};

function PaymentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [gatewayInfo, setGatewayInfo] = useState(null);
  const [verifyMsg, setVerifyMsg] = useState(null);
  const [stats, setStats] = useState({ total_paid: 0, pending_count: 0, completed_count: 0, recurring_count: 0 });

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = { page, per_page: 10 };
      if (statusFilter) params.status = statusFilter;
      const res = await paymentsAPI.list(params);
      const paymentList = res.data.payments || [];
      setPayments(paymentList);
      setTotal(res.data.total);

      // Compute quick stats from current page
      const completed = paymentList.filter(p => p.status === 'completed');
      const pending = paymentList.filter(p => p.status === 'pending');
      const recurring = paymentList.filter(p => p.is_recurring);
      setStats({
        total_paid: completed.reduce((s, p) => s + (p.amount || 0), 0),
        pending_count: pending.length,
        completed_count: completed.length,
        recurring_count: recurring.length,
      });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchPayments(); }, [page, statusFilter]);

  useEffect(() => {
    paymentsAPI.gatewayInfo().then(r => setGatewayInfo(r.data)).catch(() => {});
  }, []);

  // Paystack verification: when user returns from Paystack, URL has ?reference=xxx or ?trxref=xxx
  useEffect(() => {
    const reference = searchParams.get('reference') || searchParams.get('trxref');
    if (!reference) return;

    const verifyPayment = async () => {
      try {
        const res = await paymentsAPI.paystackVerify(reference);
        if (res.data.status === 'success') {
          setVerifyMsg({ type: 'success', text: `Payment of GH₵ ${res.data.amount?.toLocaleString()} verified successfully! Your booking is now complete.` });
        } else {
          setVerifyMsg({ type: 'error', text: `Payment verification failed. Status: ${res.data.status}. Please contact support if you were charged.` });
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        setVerifyMsg({ type: 'error', text: typeof detail === 'string' ? detail : 'Payment verification failed. Please contact support.' });
      } finally {
        // Clean up URL params
        searchParams.delete('reference');
        searchParams.delete('trxref');
        searchParams.delete('verify');
        setSearchParams(searchParams, { replace: true });
        fetchPayments();
      }
    };
    verifyPayment();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleAutoBilling = async (paymentId, currentActive) => {
    try {
      await paymentsAPI.toggleAutoBilling(paymentId, { active: !currentActive });
      fetchPayments();
    } catch (err) { console.error(err); }
  };

  const handleDownloadReceipt = async (paymentId, transactionRef) => {
    try {
      const res = await paymentsAPI.downloadReceipt(paymentId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `CampusNest_Receipt_${transactionRef}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Receipt download failed:', err);
    }
  };

  return (
    <Box>
      {verifyMsg && (
        <Alert severity={verifyMsg.type} sx={{ mb: 2 }} onClose={() => setVerifyMsg(null)}>
          {verifyMsg.text}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Payments</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage payments via {gatewayInfo?.available_gateways?.map(g => gatewayLogos[g] || g).join(' & ') || 'loading...'}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select value={statusFilter} displayEmpty onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="refunded">Refunded</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, bgcolor: '#e8f5e9' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <Box sx={{ bgcolor: '#4caf50', borderRadius: 2, p: 1.2, display: 'flex' }}><TrendingUp sx={{ color: '#fff' }} /></Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                <Typography variant="h6" fontWeight={700}>GHS {stats.total_paid.toLocaleString()}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, bgcolor: '#fff3e0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <Box sx={{ bgcolor: '#ff9800', borderRadius: 2, p: 1.2, display: 'flex' }}><Schedule sx={{ color: '#fff' }} /></Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Pending</Typography>
                <Typography variant="h6" fontWeight={700}>{stats.pending_count}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, bgcolor: '#e3f2fd' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <Box sx={{ bgcolor: '#2196f3', borderRadius: 2, p: 1.2, display: 'flex' }}><CheckCircle sx={{ color: '#fff' }} /></Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Completed</Typography>
                <Typography variant="h6" fontWeight={700}>{stats.completed_count}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, bgcolor: '#f3e5f5' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <Box sx={{ bgcolor: '#9c27b0', borderRadius: 2, p: 1.2, display: 'flex' }}><Autorenew sx={{ color: '#fff' }} /></Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Auto-billing</Typography>
                <Typography variant="h6" fontWeight={700}>{stats.recurring_count}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Supported Gateways Banner */}
      {gatewayInfo && (
        <Paper sx={{ px: 3, py: 1.5, mb: 3, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#fafafa' }}>
          <Payment color="primary" />
          <Box flex={1}>
            <Typography variant="body2" fontWeight={600}>Payment Gateways</Typography>
            <Stack direction="row" spacing={1} mt={0.5}>
              {gatewayInfo.available_gateways?.map(g => (
                <Chip key={g} label={gatewayLogos[g] || g} size="small" variant="outlined"
                  sx={{ fontWeight: 600, textTransform: 'capitalize' }} />
              ))}
            </Stack>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Currencies</Typography>
            <Typography variant="body2" fontWeight={500}>
              {gatewayInfo.supported_currencies?.slice(0, 5).join(', ')}{gatewayInfo.supported_currencies?.length > 5 ? ` +${gatewayInfo.supported_currencies.length - 5}` : ''}
            </Typography>
          </Box>
        </Paper>
      )}

      {loading && <LinearProgress sx={{ mb: 1, borderRadius: 2 }} />}

      {/* Payments Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Reference</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Amount</strong></TableCell>
              <TableCell><strong>Gateway</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Method</strong></TableCell>
              <TableCell><strong>Auto-billing</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Receipt</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <Receipt sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                  <Typography color="text.secondary">No payments found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              payments.map((p) => (
                <TableRow key={p.id} hover sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                  <TableCell>
                    <Typography fontWeight={600} variant="body2" sx={{ fontFamily: 'monospace', color: 'primary.main' }}>
                      {p.transaction_ref}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={typeLabels[p.payment_type] || p.payment_type} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={700}>{p.currency} {p.amount?.toLocaleString()}</Typography>
                    {p.refund_amount > 0 && (
                      <Typography variant="caption" color="error" display="block">
                        Refund: {p.currency} {p.refund_amount?.toLocaleString()}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={gatewayLogos[p.payment_gateway] || p.payment_gateway || '-'} size="small"
                      sx={{ fontWeight: 500, fontSize: '0.7rem' }} />
                  </TableCell>
                  <TableCell>
                    <Chip icon={statusIcons[p.status]} label={p.status?.replace('_', ' ')} size="small"
                      color={statusColors[p.status] || 'default'} sx={{ textTransform: 'capitalize', fontWeight: 500 }} />
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    {p.payment_method?.replace('_', ' ') || '-'}
                  </TableCell>
                  <TableCell>
                    {p.is_recurring ? (
                      <Box>
                        <FormControlLabel
                          control={<Switch size="small" checked={p.auto_billing_active}
                            onChange={() => handleToggleAutoBilling(p.id, p.auto_billing_active)} color="success" />}
                          label={<Typography variant="caption">{p.recurring_interval}</Typography>}
                        />
                        {p.next_billing_date && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Next: {format(new Date(p.next_billing_date), 'dd MMM yyyy')}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {p.paid_at ? format(new Date(p.paid_at), 'dd MMM yyyy') : format(new Date(p.created_at), 'dd MMM yyyy')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.paid_at ? format(new Date(p.paid_at), 'HH:mm') : format(new Date(p.created_at), 'HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {p.status === 'completed' ? (
                      <Tooltip title="Download PDF Receipt">
                        <IconButton size="small" color="primary" onClick={() => handleDownloadReceipt(p.id, p.transaction_ref)}>
                          <Download fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {total > 10 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination count={Math.ceil(total / 10)} page={page} onChange={(e, v) => setPage(v)} color="primary" shape="rounded" />
        </Box>
      )}
    </Box>
  );
}

export default PaymentsPage;
